/**
 * Empurra um evento personalizado para o `dataLayer`, para o GTM acionar as tags dele.
 *
 * ## Por que `dataLayer` e não `trackEvent`
 *
 * `utils/googleAnalytics.ts` envia direto ao GA4 pelo `gtag`. Se os eventos de cadastro passassem a
 * sair por lá, eles conviveriam com as tags `__gaawe` que o `GTM-KBF6J9QS` já dispara com os MESMOS
 * nomes — dois emissores para o mesmo evento até alguém remover as tags no painel, e a contagem
 * dobraria justamente no evento que hoje é a única ação de conversão principal do Google Ads.
 *
 * Empurrando para o `dataLayer`, o emissor continua sendo o container. O que muda é QUANDO ele
 * dispara: hoje o gatilho é um Form Submit Listener, que reage ao evento nativo de submit do
 * `<form>` — e o submit nativo acontece a cada clique no botão, inclusive quando o
 * `react-hook-form` rejeita na validação e nem chega a chamar o `onSubmit`.
 *
 * Medido no GA4 em 28 dias (9/ago a 5/set de 2026): `envio form - cadastro usuario` disparou 7.472
 * vezes para 3.450 pessoas — 2,23 por pessoa —, contra 1,13 e 1,06 dos outros dois passos do MESMO
 * formulário. O passo 1 é onde se erra e-mail e documento, corrige e reenvia.
 *
 * ## Ordem de implantação
 *
 * Este código é INERTE até alguém repontar os gatilhos no painel do GTM: nada escuta estes eventos
 * hoje. É de propósito — o inverso (mudar o gatilho antes de o código existir) apagaria a medição
 * de cadastro no intervalo entre as duas mudanças.
 */

/**
 * Nomes que o painel do GTM precisa passar a escutar. Ficam aqui, e não soltos na página, para que
 * quem for repontar os gatilhos tenha uma lista só para conferir — e para que renomear um deles no
 * código quebre o import, em vez de silenciosamente parar de medir.
 */
export const GTM_EVENTS = {
  /** Passo 1 do cadastro concluído com validação OK (era: Form Submit no `#register-form-userinfo`). */
  passoUsuario: 'cadastro_passo_usuario',
  /** Passo 2 concluído com validação OK. */
  passoDocumento: 'cadastro_passo_documento',
  /** Passo "Audiência" concluído. */
  passoAudiencia: 'cadastro_passo_audiencia',
  /** Passo 3 concluído com validação OK — ainda ANTES da chamada que cria a conta. */
  passoSenha: 'cadastro_passo_senha',
  /**
   * A conta existe: `register` respondeu sem erro.
   *
   * É este — e não `passoUsuario` — que deveria ser a conversão do Google Ads. Hoje a ação
   * principal da conta é `envio form - cadastro usuario`, ou seja, o PRIMEIRO dos três passos:
   * conta quem começou o cadastro como se tivesse terminado. Em 28 dias, 3.450 pessoas fizeram o
   * passo 1 e o banco registrou ~2.045 cadastros no mês.
   */
  cadastroCriado: 'cadastro_criado',
} as const;

export type GtmEventName = (typeof GTM_EVENTS)[keyof typeof GTM_EVENTS];

/**
 * NUNCA carregar PII aqui — nem nome, e-mail, documento ou token. O `dataLayer` é legível por
 * qualquer script da página, e o que entra nele sai para todas as tags do container.
 *
 * `params` existe para identificador técnico que uma tag precisa junto do evento — hoje, o
 * `eventID` aleatório do `Lead` do Meta (`utils/metaLeadEvent`). Pela mesma regra: nada que diga
 * quem a pessoa é.
 */
export const pushGtmEvent = (
  event: GtmEventName,
  params?: Record<string, string>,
) => {
  if (typeof window === 'undefined') return;

  // O array pode ainda não existir: o `TagManager.initialize` roda num efeito do `_app`, e um passo
  // do cadastro pode concluir antes disso numa conexão lenta. Criar aqui é o que o próprio snippet
  // do GTM faz, e o container consome o que já estiver na fila quando carregar.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...params, event });
};
